import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';

import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classes from './GeneralCard.module.css';

import Clickable from '../../UI/Clickable/Clickable';
import isMobileViewHook from '../../../hooks/isMobileView';

import DownloadButton from '../../UI/DownloadButton/DownloadButton';
import {
  parseContentType,
  downloadSrc,
  parseLocationToPath,
} from '../../../utils';
import googleAnalytics from '../../../utils/googleAnalytics';

const GeneralCard = ({ content, onSelect, color, onBackToTop }) => {
  const mobile = isMobileViewHook();
  const [loadingDownload, setLoadingDownload] = useState(false);
  const { media: foundMedia, title, thumbnail } = content;
  const hub = useSelector((state) => state.hub.hub);

  const location = useLocation();
  const currentPath = parseLocationToPath(location);
  useEffect(() => {
    if (currentPath.search('home') === -1) {
      onBackToTop();
    }
  }, [currentPath, onBackToTop]);
  // TOD0: remove this when the missing videos are uploaded
  let media = foundMedia;
  if (!media) media = { mime: 'video/mp4' };

  const downloadHandler = async () => {
    setLoadingDownload(true);
    try {
      await downloadSrc(media.url, title);
      setLoadingDownload(false);
      googleAnalytics({
        event: 'file_download',
        file_url: content.media.url,
        file_name: title,
        section_title: hub.name,
      });
    } catch {
      setLoadingDownload(false);
    }
  };

  const fileType = parseContentType(media.mime);

  const renderThumbnail = () => {
    switch (fileType) {
      case 'video':
        return thumbnail && thumbnail !== null ? (
          <Clickable
            onClick={() => {
              onSelect(content);
              const finalThumb = thumbnail ? thumbnail[0].url : media.url;
              googleAnalytics({
                event: fileType === 'video' ? 'video_play' : 'view_image',
                file_url: fileType === 'video' ? media.url : finalThumb,
                file_name: content.title,
                section_title: hub.name,
              });
            }}
          >
            <img className={classes.image} src={thumbnail[0].url} alt={title} />
          </Clickable>
        ) : (
          <video
            data-testid="video"
            autoPlay={false}
            onPlay={() => {
              onSelect(content);
              const finalThumb = thumbnail ? thumbnail[0].url : media.url;
              googleAnalytics({
                event: fileType === 'video' ? 'video_play' : 'view_image',
                file_url: fileType === 'video' ? media.url : finalThumb,
                file_name: content.title,
                section_title: hub.name,
              });
            }}
          >
            <source type={media.mime} src={media.url} />
            <track kind="captions" />
            Your browser does not support the video tag.
          </video>
        );
      default:
      case 'image':
        return (
          <Clickable
            onClick={() => {
              onSelect(content);
              const finalThumb = thumbnail ? thumbnail[0].url : media.url;
              googleAnalytics({
                event: fileType === 'video' ? 'video_play' : 'view_image',
                file_url: fileType === 'video' ? media.url : finalThumb,
                file_name: content.title,
                section_title: hub.name,
              });
            }}
          >
            <img
              className={classes.image}
              src={thumbnail ? thumbnail[0].url : media.url}
              alt={title}
            />
          </Clickable>
        );
    }
  };

  return (
    <div className={classes.card}>
      <div className={classes.thumbnail}>
        {renderThumbnail()}
        <Clickable
          onClick={() => {
            onSelect(content);
            const finalThumb = thumbnail ? thumbnail[0].url : media.url;
            googleAnalytics({
              event: fileType === 'video' ? 'video_play' : 'view_image',
              file_url: fileType === 'video' ? media.url : finalThumb,
              file_name: content.title,
              section_title: hub.name,
            });
          }}
          className={classes.banner}
        >
          {fileType === 'video' && (
            <img
              className={classes.playButtonImage}
              src="/images/play.svg"
              alt="play"
            />
          )}
          <p className={classes.bannerText}>
            {fileType === 'video' ? 'Watch Video' : 'View Photo'}
          </p>
        </Clickable>
      </div>
      <h2 className={classes.title}>{title}</h2>
      <div
        className={
          mobile ? classes.boxDownloadButtonMobile : classes.boxDownloadButton
        }
      >
        <DownloadButton
          color={color}
          downloadable
          loadingDownload={loadingDownload}
          onDownload={downloadHandler}
          text="Download"
        />
      </div>
    </div>
  );
};

GeneralCard.propTypes = {
  content: PropTypes.shape({
    viewType: PropTypes.oneOf(['FILE', 'GENERAL']),
    thumbnail: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
      })
    ),
    media: PropTypes.shape({ url: PropTypes.string, mime: PropTypes.string }),
    description: PropTypes.string,
    enable: PropTypes.bool,
    id: PropTypes.number,
    size: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func,
  color: PropTypes.string.isRequired,
  onBackToTop: PropTypes.func,
};

GeneralCard.defaultProps = {
  onSelect: () => {},
  onBackToTop: () => {},
};

export default GeneralCard;
